/**
 * "Is this card playable under the bonus I am working off" — the lobby half of
 * the promotion play restriction (#434).
 *
 * A promotion names the providers it allows by their `game.providers.id`, so the
 * lobby has to speak the same identifier. Every catalog card carries one. The
 * seven Originals cards do NOT: they are built from the canonical `ORIGINALS`
 * list, not the catalog, and they carry the game CODE in `gameType` because that
 * is what picks their artwork. Both are resolved here — the provider row from
 * the providers list the lobby already loads, the canonical type from the same
 * static map svc-game applies (`originals/adapter.ts`) — so a card cannot be
 * judged against the wrong identity and silently stay open.
 */
import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GAMES_QUERY_KEYS, fetchProviders } from '../games/api.js';
import { ORIGINALS_ART } from '../originals/catalog.js';
import { isGameLockedByPromo, usePlayRestrictions } from './play-restrictions.js';

export interface LockableCard {
  code?: string | undefined;
  gameType?: string | null | undefined;
  providerId?: string | undefined;
  providerCode?: string | undefined;
  providerDisplayName?: string | undefined;
}

/**
 * The canonical catalog game type of an Originals game. Mirrors
 * `GAME_TYPE_MAP` in `apps/game/src/originals/adapter.ts`: only Card Flip is
 * 'card', everything else lands in 'other'. A card judged against its display
 * code ('wheel') instead would be locked by every type-restricted promotion.
 */
export function originalsCanonicalGameType(code: string): 'card' | 'other' {
  return code === 'cards' ? 'card' : 'other';
}

export function isOriginalsCard(game: LockableCard): boolean {
  if (game.providerCode === 'originals') return true;
  if (game.providerDisplayName === 'Originals') return true;
  return !!game.code && !!ORIGINALS_ART[game.code];
}

export interface GameLockResult {
  /** True while at least one live bonus restricts what may be played. */
  restricted: boolean;
  isLocked: (game: LockableCard) => boolean;
}

/**
 * Set once per page by `GamePromoLockProvider`. Tiles read the verdict from here
 * rather than each opening their own react-query subscription: the lottery and
 * casino grids render a few hundred cards at a time, and the per-tile hook put
 * that many observers on two shared query keys for one answer they all agree on
 * (#524).
 */
export const GamePromoLockContext = createContext<GameLockResult | null>(null);

export function useGamePromoLockValue(): GameLockResult {
  const restrictions = usePlayRestrictions();
  // Only when something is actually restricted, and it is the key the lobby
  // already loads — so the common case costs no request at all.
  const providersQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.providers,
    queryFn: fetchProviders,
    staleTime: 60_000,
    enabled: restrictions.restricted,
  });

  const originalsProviderId = useMemo(() => {
    const items = providersQuery.data?.items ?? [];
    return items.find((p) => p.code === 'originals' || p.displayName === 'Originals')?.id;
  }, [providersQuery.data]);

  return useMemo(
    () => ({
      restricted: restrictions.restricted,
      isLocked: (game: LockableCard) => {
        if (!restrictions.restricted) return false;
        const original = isOriginalsCard(game);
        const providerId = game.providerId ?? (original ? originalsProviderId : undefined);
        const gameType = original
          ? originalsCanonicalGameType(game.code ?? '')
          : (game.gameType ?? null);
        return isGameLockedByPromo(restrictions, { providerId, gameType });
      },
    }),
    [restrictions, originalsProviderId],
  );
}

/**
 * The verdict for one card.
 *
 * Outside a provider it subscribes on its own instead of answering "unlocked" —
 * a card that renders somewhere the shell does not wrap must not silently be the
 * one surface that opens a game the rest of the lobby locks. Whether a provider
 * sits above a given tile is fixed for that tile's whole life (it is mounted at
 * the shell), so the branch never changes between renders.
 */
export function useGamePromoLock(): GameLockResult {
  const shared = useContext(GamePromoLockContext);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return shared ?? useGamePromoLockValue();
}
